/**
 * Déblocage par email (calculateur, checklists, templates, replays, newsletter).
 *
 * - Avant tout envoi : validation des champs (klapi-validation.js) et filtre anti-robot (champ piège,
 *   envoi en moins de 2 s). Un envoi refusé ne débloque rien ; un robot croit que ça a marché.
 * - Formulaires [data-deblocage] : envoi AJAX du formulaire client Shopify vers /contact.
 *   Si Shopify renvoie un défi anti-spam (redirection /challenge), on laisse l'envoi classique se faire.
 * - Visiteur débloqué si : localStorage klapi_debloque = 1, ou client connecté inscrit
 *   (window.klapiConnu, posé en Liquid), ou retour de formulaire (?customer_posted=true).
 * - Au déblocage : html[data-debloque], événement `klapi:debloque` ({ source, ressource }),
 *   GA4 generate_lead (une seule fois par envoi).
 *
 * API : window.klapiDeblocage.estDebloque()
 */
(() => {
  if (window.klapiDeblocage) return;

  const CLE = 'klapi_debloque';
  const memoire = {
    lire() {
      try {
        return window.localStorage.getItem(CLE) === '1';
      } catch (e) {
        return false;
      }
    },
    ecrire() {
      try {
        window.localStorage.setItem(CLE, '1');
      } catch (e) {
        /* stockage indisponible : le déblocage vaut pour la page */
      }
    },
  };

  const suivre = (evenement, donnees = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: evenement, ...donnees });
    if (typeof window.gtag === 'function') window.gtag('event', evenement, donnees);
  };

  let debloque = memoire.lire() || window.klapiConnu === true;

  function debloquer(detail = {}) {
    debloque = true;
    memoire.ecrire();
    document.documentElement.setAttribute('data-debloque', '');
    document.dispatchEvent(new CustomEvent('klapi:debloque', { detail }));
  }

  /* Validation (klapi-validation.js) ------------------------------------- */

  let jetables = null;
  async function chargerJetables() {
    if (jetables) return jetables;
    try {
      const liste = await fetch(window.klapiJetablesUrl).then((r) => r.json());
      jetables = new Set(liste);
    } catch (e) {
      jetables = new Set();
    }
    return jetables;
  }

  // Heure d'affichage de chaque formulaire (envoi en moins de 2 s = robot)
  const affiches = new WeakMap();
  const noterAffichage = () =>
    document.querySelectorAll('[data-deblocage]').forEach((form) => affiches.has(form) || affiches.set(form, Date.now()));

  function afficherErreur(form, cle, message, champ) {
    const zone = form.querySelector(`[data-erreur-pour="${cle}"]`);
    zone.textContent = message;
    zone.hidden = false;
    champ?.setAttribute('aria-invalid', 'true');
  }

  function effacerErreurs(form) {
    form.querySelectorAll('[data-erreur-pour]').forEach((zone) => {
      zone.hidden = true;
      zone.textContent = '';
    });
    form.querySelectorAll('[aria-invalid]').forEach((champ) => champ.removeAttribute('aria-invalid'));
  }

  /** Vrai si le formulaire peut partir ; sinon les erreurs sont affichées sous les champs. */
  async function valider(form) {
    const v = window.klapiValidation;
    effacerErreurs(form);
    if (!v) return true;
    let premier = null;
    for (const cle of ['prenom', 'nom']) {
      const champ = form.querySelector(`[data-champ="${cle}"]`);
      if (!champ) continue;
      const r = v.verifierNom(champ.value);
      if (r.erreur) {
        afficherErreur(form, cle, r.message, champ);
        premier = premier || champ;
      }
    }
    const champEmail = form.querySelector('[data-champ="email"]');
    const r = v.verifierEmail(champEmail.value, await chargerJetables());
    if (r.erreur) {
      afficherErreur(form, 'email', r.message, champEmail);
      premier = premier || champEmail;
    } else if (r.suggestion && form.dataset.suggestionVue !== champEmail.value.trim().toLowerCase()) {
      // Faute de frappe : on propose la correction une fois, sans bloquer un second envoi
      form.dataset.suggestionVue = champEmail.value.trim().toLowerCase();
      const zone = form.querySelector('[data-erreur-pour="email"]');
      zone.textContent = 'Vous vouliez dire ';
      const lien = document.createElement('button');
      lien.type = 'button';
      lien.className = 'k-deblocage__suggestion';
      lien.textContent = r.suggestion;
      lien.addEventListener('click', () => {
        champEmail.value = r.suggestion;
        zone.hidden = true;
        champEmail.focus();
      });
      zone.append(lien, '\u00a0?');
      zone.hidden = false;
      premier = premier || champEmail;
    }
    if (premier) {
      premier.focus();
      return false;
    }
    return true;
  }

  /** Robot : champ piège rempli, ou envoi moins de 2 s après l'affichage. */
  function estRobot(form) {
    const piege = form.querySelector('[data-piege]');
    const depuis = Date.now() - (affiches.get(form) || 0);
    return (piege && piege.value.trim() !== '') || depuis < 2000;
  }

  async function envoyer(form) {
    const bouton = form.querySelector('[type="submit"]');
    if (!(await valider(form))) return;

    // Robot : on fait semblant que ça marche, sans rien envoyer ni débloquer
    if (estRobot(form)) {
      form.querySelectorAll('input, button').forEach((el) => (el.disabled = true));
      bouton.querySelector('span').textContent = 'Merci !';
      return;
    }

    bouton.setAttribute('aria-busy', 'true');
    bouton.disabled = true;

    try {
      const reponse = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });
      if (!reponse.ok || (reponse.redirected && /\/challenge/.test(reponse.url))) {
        // Défi anti-spam (403 ou redirection /challenge) : envoi classique, retour sur #klapi-deblocage
        form.dataset.classique = '';
        form.requestSubmit();
        return;
      }
      const detail = { source: form.dataset.source, ressource: form.dataset.ressource, form };
      suivre('generate_lead', { source: form.dataset.source, ressource: form.dataset.ressource });
      debloquer(detail);
    } catch (e) {
      form.dataset.classique = '';
      form.requestSubmit();
    } finally {
      bouton.removeAttribute('aria-busy');
      bouton.disabled = false;
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-deblocage]');
    if (!form || form.dataset.classique !== undefined) return;
    event.preventDefault();
    envoyer(form);
  });

  // Retour d'un envoi classique
  if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
    if (!debloque) suivre('generate_lead', { source: 'retour-formulaire' });
    debloque = true;
    memoire.ecrire();
  }

  if (debloque) document.documentElement.setAttribute('data-debloque', '');

  noterAffichage();
  document.addEventListener('DOMContentLoaded', noterAffichage);
  // Formulaires ouverts plus tard (popup) : l'affichage compte à partir de l'ouverture
  document.addEventListener('toggle', noterAffichage, true);
  document.addEventListener('focusin', (event) => {
    const form = event.target.closest?.('[data-deblocage]');
    if (form && !affiches.has(form)) affiches.set(form, Date.now());
  });

  window.klapiDeblocage = { estDebloque: () => debloque, debloquer, suivre };
})();
